import React, { useState, useEffect } from "react";
import { useAuth } from "../../AuthContext"; // ✅ Import authentication context
import { useNavigate } from "react-router-dom";
import "../../style/UserSettings.css"; // ✅ Ensure we create a corresponding CSS file

const API_BASE_URL = process.env.REACT_APP_API_SOURCE;

const UserSettings = () => {
    const { userId, deleteAccount, setUsername, logout, profilePicture, setProfilePicture } = useAuth(); // ✅ Added logout function
    const navigate = useNavigate();
    const [showPopup, setShowPopup] = useState(false); // Controls visibility of confirmation popup
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    
    const [newUsername, setNewUsername] = useState(""); // State for the new username
    const [usernameLoading, setUsernameLoading] = useState(false);
    const [usernameMessage, setUsernameMessage] = useState("");

    // New state for profile picture upload & preview
    // We assume that the backend stores and returns a relative path (e.g. "/profileUploads/uniqueFile.jpg")
    //const [profilePicture, setProfilePicture] = useState(localStorage.getItem("profilePicture") || "");
    const [selectedProfileFile, setSelectedProfileFile] = useState(null);

    // On mount, you might fetch the profile picture from backend (if desired)
    // For simplicity, we rely on localStorage (and backend update when file is uploaded)

    useEffect(() => {
  if (userId) {
    fetch(`${API_BASE_URL}/backend/api/Settings/fetch_profile_picture.php?userId=${userId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success" && data.profilePicture) {
          setProfilePicture(data.profilePicture);
          localStorage.setItem("profilePicture", data.profilePicture);
        }
      })
      .catch((err) => console.error("Error fetching profile picture:", err));
  }
}, [userId]);

    // Handle file selection and preview for profile picture
    const handleProfileFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedProfileFile(file);
            setProfilePicture(URL.createObjectURL(file)); // Show a temporary preview
        }
    };

    // Handle uploading profile picture to backend
    const handleUpdateProfilePhoto = async () => {
        if (!selectedProfileFile) {
            alert("Please select an image file.");
            return;
        }
        const formData = new FormData();
        formData.append("userId", userId);
        formData.append("profilePicture", selectedProfileFile);

        try {
            const response = await fetch(`${API_BASE_URL}/backend/api/Settings/upload_profile_picture.php`, {
                method: "POST",
                body: formData, // Browser sets multipart/form-data automatically
            });
            const result = await response.json();
            if (result.status === "success") {
                // Here, result.profilePicture is assumed to be the relative path (e.g., "/profileUploads/uniqueFile.jpg")
                console.log(result.profilePicture);                
                setProfilePicture(result.profilePicture);
                localStorage.setItem("profilePicture", result.profilePicture);

                alert("Profile picture updated successfully!");
            } else {
                alert(result.message);
            }
        } catch (error) {
            console.error("Error uploading profile picture:", error);
            alert("Error uploading profile picture.");
        }
    };

    // Handles the username update request (sends confirmation email)
    const handleUpdateUsername = async () => {
        setUsernameLoading(true);
        setUsernameMessage("");
        setError("");

        try {
            const response = await fetch(`${API_BASE_URL}/backend/api/Settings/request_username_change.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId, newUsername }),
            });

            const text = await response.text();
            const result = JSON.parse(text);
            setUsernameLoading(false);

            if (result.status === "success") {
                setUsernameMessage("A confirmation email has been sent to your UB email. Please check your email to complete the username change.");
            } else {
                setError(result.message);
            }
        } catch (error) {
            console.error("Username Update Error:", error);
            setError("An error occurred. Please try again.");
        }

        setUsernameLoading(false);
    };

    // Handles the account deletion request
    const handleDeleteAccount = async () => {
        setLoading(true);
        setError("");

        try {
            const response = await fetch(`${API_BASE_URL}/backend/api/Settings/delete_user.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId }),
            });

            const result = await response.json();
            console.log("Delete Response:", result);

            if (result.status === "success") {
                alert("Account deleted successfully!");
                if (logout && typeof logout === "function") {
                    logout();
                    navigate("/login");
                } else {
                    console.error("logout is not a function");
                }
                navigate("/login", { replace: true });
            } else {
                setError(result.message);
            }
        } catch (error) {
            console.error("Delete Error:", error);
            setError("An error occurred. Please try again.");
        }

        setLoading(false);
        setShowPopup(false);
    };

    // Password Change Functionality
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [passwordMessage, setPasswordMessage] = useState("");
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Password Validation Function
    const validatePassword = (password) => {
        return (
            password.length >= 8 &&
            /[A-Z]/.test(password) &&
            /[0-9]/.test(password) &&
            /[\W]/.test(password)
        );
    };

    // Handle Password Change (sends confirmation email)
    const handleChangePassword = async () => {
        setPasswordLoading(true);
        setPasswordMessage("");
        setError("");

        if (!validatePassword(newPassword)) {
            setError("Password must be at least 8 characters long, contain at least one uppercase letter, one number, and one special character.");
            setPasswordLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/backend/api/Settings/request_password_change.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId, currentPassword, newPassword }),
            });

            const result = await response.json();
            console.log("Password Change Response:", result);

            if (result.status === "success") {
                setPasswordMessage("A confirmation email has been sent to your UB email. Please check your email to complete the password change.");
            } else {
                setError(result.message);
            }
        } catch (error) {
            console.error("Password Change Error:", error);
            setError("An error occurred. Please try again.");
        }

        setPasswordLoading(false);
    };

    return (
        <div className="user-settings-container">
            <div className="user-settings-box">
                <h1>User Settings</h1>
                <p>Manage your account settings here.</p>

                {/* Profile Picture Section */}
                <div className="profile-picture-section">
                    <h2>Profile Picture</h2>
                    <img
                        src={profilePicture ? profilePicture: `${API_BASE_URL}/backend/profileUploads/default_profile.png`}
                        alt="Profile"
                        className="profile-picture-preview"
                    />
                    <input type="file" accept="image/*" onChange={handleProfileFileChange} />
                    <button className="update-profile-btn" onClick={handleUpdateProfilePhoto}>
                        Update Profile Photo
                    </button>
                </div>

                {/* Change Username Section */}
                <div className="change-username-section">
                    <input
                        type="text"
                        placeholder="Enter new username"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        className="username-input"
                    />
                    <button
                        className="update-username-btn"
                        onClick={handleUpdateUsername}
                        disabled={usernameLoading || newUsername.length < 3}
                    >
                        {usernameLoading ? "Updating..." : "Change Username"}
                    </button>
                    {usernameMessage && <p className="success-message">{usernameMessage}</p>}
                    {error && <p className="error-message">{error}</p>}
                </div>

                {/* Change Password Section */}
                <div className="change-password-section">
                    <input
                        type="password"
                        placeholder="Current Password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="password-input"
                    />
                    <input
                        type="password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="password-input"
                    />
                    <button
                        className="update-password-btn"
                        onClick={handleChangePassword}
                        disabled={passwordLoading || newPassword.length < 8}
                    >
                        {passwordLoading ? "Updating..." : "Change Password"}
                    </button>
                    {passwordMessage && <p className="success-message">{passwordMessage}</p>}
                    {error && <p className="error-message">{error}</p>}
                </div>

                {/* Delete Account Button */}
                <button className="delete-account-btn" onClick={() => setShowPopup(true)}>
                    Delete Account
                </button>

                {/* Confirmation Popup */}
                {showPopup && (
                    <div className="popup-overlay">
                        <div className="popup-box">
                            <h2>Are you sure?</h2>
                            <p>Deleting your account is permanent and cannot be undone.</p>
                            {error && <p className="error-message">{error}</p>}
                            <div className="popup-buttons">
                                <button onClick={() => setShowPopup(false)} className="cancel-btn">
                                    Cancel
                                </button>
                                <button onClick={handleDeleteAccount} className="confirm-btn" disabled={loading}>
                                    {loading ? "Deleting..." : "Confirm"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserSettings;